import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { axiosInstance } from "../../lib/axios";
import { buildApiUrl } from "../../lib/utils";
import { usePresence } from "../presence/PresenceContext";

const MessagesPanelContext = createContext(null);

export const useMessagesPanel = () => {
  const ctx = useContext(MessagesPanelContext);
  if (!ctx) {
    throw new Error("useMessagesPanel must be used within MessagesPanelProvider");
  }
  return ctx;
};

export const MessagesPanelProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [view, setView] = useState("list"); // 'list' | 'conversation'

  const hasFetchedRef = useRef(false);
  const typingStopTimerRef = useRef(null);
  const { announceTyping } = usePresence();

  // Extract user ID from localStorage
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        setCurrentUserId(user.id);
      } catch {
        // ignore
      }
    }
  }, []);

  // Fetch conversations (lazy — only on first open)
  const fetchConversations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        buildApiUrl("/api/v1/messages/conversations")
      );
      setConversations(response.data.conversations || []);
    } catch (err) {
      console.error("MessagesPanel: error fetching conversations", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshConversations = useCallback(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Fetch messages for a conversation
  const fetchMessages = useCallback(async (conversationId) => {
    try {
      setMessagesLoading(true);
      const response = await axiosInstance.get(
        buildApiUrl(
          `/api/v1/messages/conversations/${conversationId}/messages`
        )
      );
      setMessages(response.data.messages || []);
    } catch (err) {
      console.error("MessagesPanel: error fetching messages", err);
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  // Panel controls
  const openPanel = useCallback(() => {
    setIsOpen(true);
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchConversations();
    }
  }, [fetchConversations]);

  const closePanel = useCallback(() => {
    setIsOpen(false);
  }, []);

  const togglePanel = useCallback(() => {
    setIsOpen((prev) => {
      const next = !prev;
      if (next && !hasFetchedRef.current) {
        hasFetchedRef.current = true;
        fetchConversations();
      }
      return next;
    });
  }, [fetchConversations]);

  // Conversation navigation
  const selectConversation = useCallback(
    (conv) => {
      setSelectedConversation(conv);
      setView("conversation");
      setNewMessage("");
      fetchMessages(conv.id);
    },
    [fetchMessages]
  );

  const goBackToList = useCallback(() => {
    setSelectedConversation(null);
    setMessages([]);
    setView("list");
    setNewMessage("");
  }, []);

  // Send message via REST (same as messages-page.js handleSendMessage)
  const sendMessage = useCallback(
    async (content) => {
      if (!content.trim() || !selectedConversation || sending) return;
      try {
        setSending(true);
        await axiosInstance.post(buildApiUrl("/api/v1/messages/"), {
          conversation_id: selectedConversation.id,
          content: content.trim(),
        });
        setNewMessage("");
        // Refresh messages after send
        const response = await axiosInstance.get(
          buildApiUrl(
            `/api/v1/messages/conversations/${selectedConversation.id}/messages`
          )
        );
        setMessages(response.data.messages || []);
      } catch (err) {
        console.error("MessagesPanel: error sending message", err);
      } finally {
        setSending(false);
      }
    },
    [selectedConversation, sending]
  );

  // Handle incoming real-time message from PlatformSocketBridge
  const handleIncomingMessage = useCallback(
    (msg) => {
      if (!msg || typeof msg !== "object") return;
      const convId = msg.conversation_id;
      if (!convId) return;

      // Update the conversation list's last_message preview
      setConversations((prev) =>
        prev.map((c) =>
          c.id === convId
            ? {
                ...c,
                last_message: {
                  content: msg.message || msg.content || "",
                  created_at: msg.timestamp || new Date().toISOString(),
                },
              }
            : c
        )
      );

      // If this conversation is currently selected, append the message
      if (selectedConversation && selectedConversation.id === convId) {
        const senderId =
          msg.sender_id ||
          (selectedConversation.participants || []).find(
            (p) => p !== currentUserId
          );
        setMessages((prev) => [
          ...prev,
          {
            conversation_id: convId,
            sender_id: senderId,
            content: msg.message || msg.content || "",
            message_type: "text",
            attachments: [],
            status: "read",
            created_at: msg.timestamp || new Date().toISOString(),
            reply_to: null,
            edited: false,
            sender_info: {},
          },
        ]);
      }
    },
    [selectedConversation, currentUserId]
  );

  // Typing announcement helper
  const announceTypingInPanel = useCallback(
    (isTyping) => {
      if (!selectedConversation?.id) return;
      announceTyping(selectedConversation.id, isTyping);
      if (typingStopTimerRef.current) {
        clearTimeout(typingStopTimerRef.current);
      }
      if (isTyping) {
        typingStopTimerRef.current = setTimeout(() => {
          announceTyping(selectedConversation.id, false);
        }, 2000);
      }
    },
    [selectedConversation, announceTyping]
  );

  const value = {
    isOpen,
    conversations,
    selectedConversation,
    messages,
    loading,
    messagesLoading,
    newMessage,
    setNewMessage,
    sending,
    currentUserId,
    view,
    openPanel,
    closePanel,
    togglePanel,
    selectConversation,
    goBackToList,
    sendMessage,
    handleIncomingMessage,
    refreshConversations,
    announceTypingInPanel,
  };

  return (
    <MessagesPanelContext.Provider value={value}>
      {children}
    </MessagesPanelContext.Provider>
  );
};
